/**
 * API methods that rely on both Duolingo TTS and TTS Generation Backend
 */

import Axios from "axios";

import type { DuolingoTtsProvider } from "./duolingoTts";
import {
  getDuolingoTtsRecordedVoices,
  getDuolingoTtsVoices,
} from "./duolingoTts";
import type {
  TtsGenerationAge,
  TtsGenerationCharacter,
  TtsGenerationGender,
  TtsGenerationProvider,
  TtsGenerationStatus,
} from "./ttsGenerationBackend";
import { getTtsGenerationVoiceList } from "./ttsGenerationBackend";

export interface VoiceListVoice {
  addedAt?: Date;
  age?: TtsGenerationAge;
  alias: string;
  character?: TtsGenerationCharacter;
  duolingoLanguageId?: string;
  dynamicProperties?: Record<string, string>;
  gender?: TtsGenerationGender;
  id: string;
  isExpressive: boolean;
  isMultilingual: boolean;
  isUsedInLearningApp: boolean;
  languageId: string;
  languageStatuses: { language: string; qa_status: string }[];
  name: string;
  notes: string;
  provider: TtsGenerationProvider | "RECORDED";
  providerParameters?: Record<string, string | number | boolean | null>;
  status: TtsGenerationStatus;
}

const areProvidersEqual = (
  ttsGenerationProvider: TtsGenerationProvider,
  duolingoTtsProvider: DuolingoTtsProvider,
) =>
  (ttsGenerationProvider === "AMAZON" && duolingoTtsProvider === "ivona") ||
  ttsGenerationProvider.toUpperCase() === duolingoTtsProvider.toUpperCase();

export const getCombinedVoiceList = async (): Promise<VoiceListVoice[]> => {
  const [ttsGenList, duolingoTtsList, recordedVoicesList] = await Promise.all([
    getTtsGenerationVoiceList(),
    getDuolingoTtsVoices(),
    getDuolingoTtsRecordedVoices(),
  ]);

  const ttsVoices = ttsGenList.map(ttsGenVoice => {
    const isUsedInLearningApp =
      duolingoTtsList?.find(
        duolingoTtsVoice =>
          // Amazon and Azure custom voices match on their name,
          // Google and Azure normal voices on their alias
          [ttsGenVoice.name, ttsGenVoice.alias].includes(
            duolingoTtsVoice.name,
          ) &&
          // It is possible that a provider has a voice whose short name overlaps with our name
          // for a voice of a different provider in a different language (e.g., Microsoft's
          // en-US-CoraNeural and Google's el-GR-Wavenet-A are both Cora)
          areProvidersEqual(ttsGenVoice.provider, duolingoTtsVoice.provider),
      ) !== undefined;

    return {
      ...ttsGenVoice,
      isUsedInLearningApp,
    };
  });
  const recordedVoices =
    recordedVoicesList?.map(recordedVoice => ({
      age: recordedVoice.age?.toUpperCase() as TtsGenerationAge,
      alias: recordedVoice.name,
      duolingoLanguageId: recordedVoice.language,
      gender: recordedVoice.gender?.toUpperCase() as TtsGenerationGender,
      id: recordedVoice.name,
      isExpressive: false,
      isMultilingual: false,
      isUsedInLearningApp: true,
      languageId: recordedVoice.language,
      languageStatuses: [],
      name: recordedVoice.name,
      notes: "",
      provider: "RECORDED" as const,
      status: "ACTIVE" as const,
    })) ?? [];
  return [...ttsVoices, ...recordedVoices];
};

export const getAudioMp3 = async (url: string) =>
  (
    await Axios.get<Blob>(url, {
      responseType: "blob",
    })
  ).data;
